import type {AxiosInstance} from "axios";

import {axiosInstance as client} from "./axiosInstance";
import type ContentIssueResolutionStatement from "../courses/ContentIssueResolutionStatement";
import type ContentIssueSearchResult from "../courses/ContentIssueSearchResult";
import type ContentIssueType from "../courses/ContentIssueType";
import type NewContentIssue from "../courses/NewContentIssue";
import type {Page, PagedResponse} from "../../helpers/paginatedSearchHelpers";
import {createPage} from "../../helpers/paginatedSearchHelpers";
import type User from "../users/User";

type ChapterContentIssueStats = {
	chapterId: number;
	stats: ExerciseContentIssueStats[];
};

export type ExerciseContentIssueStats = {
	exerciseId: number;
	numberOfIssues: number;
};

function createService(client: AxiosInstance) {
	async function getStatisticsOnContentIssues(
		courseId: number,
		criteria: {
			type?: ContentIssueType[];
			resolutionStatus?: (ContentIssueResolutionStatement | "not_resolved")[];
		}
	): Promise<ChapterContentIssueStats[]> {
		const url = `/api/courses/${courseId}/content-issue-statistics`;

		const params = new URLSearchParams();

		criteria.type?.forEach((t) => params.append("type", t));

		criteria.resolutionStatus?.forEach((t) =>
			params.append("resolutionStatus", t)
		);

		const {data} = await client.get(url, {params});

		return data.content;
	}

	async function reportContentIssue(courseId: number, issue: NewContentIssue) {
		await client.post(`/api/courses/${courseId}/content-issues`, issue);
	}

	async function resolveContentIssue(
		courseId: number,
		issueId: string,
		resolution: {statement: "fixed" | "irrelevant"}
	) {
		await client.put(
			`/api/courses/${courseId}/content-issues/${issueId}/resolution`,
			resolution
		);
	}

	async function cancelContentIssueResolution(
		courseId: number,
		issueId: string
	) {
		await client.delete(
			`/api/courses/${courseId}/content-issues/${issueId}/resolution`
		);
	}

	async function searchContentIssues(
		courseId: number,
		criteria: {
			chapterId?: number;
			exerciseId?: number;
			type?: ContentIssueType[];
			resolutionStatus?: (ContentIssueResolutionStatement | "not_resolved")[];
		},
		sort: {field: string; descending: boolean},
		pageSize: number
	): Promise<
		Page<ContentIssueSearchResult, string, {reporter: Map<number, User>}>
	> {
		const url = `/api/courses/${courseId}/content-issues`;

		const params = new URLSearchParams();

		params.append("pageSize", pageSize.toString());
		params.append("sort", sort.descending ? `-${sort.field}` : sort.field);

		if (criteria.chapterId) {
			params.append("chapterId", criteria.chapterId.toString());
		}
		if (criteria.exerciseId) {
			params.append("exerciseId", criteria.exerciseId.toString());
		}

		criteria.type?.forEach((t) => params.append("type", t));

		criteria.resolutionStatus?.forEach((t) =>
			params.append("resolutionStatus", t)
		);

		const {data} = await client.get<
			PagedResponse<ContentIssueSearchResult, {reporter: User[]}>
		>(url, {params});

		return createPage(client, data, {
			preprocessRelated: (related) => ({
				reporter: related.reporter.reduce(
					(m, r) => m.set(r.id, r),
					new Map<number, User>()
				),
			}),
		});
	}

	return {
		cancelContentIssueResolution: cancelContentIssueResolution,
		getStatisticsOnContentIssues: getStatisticsOnContentIssues,
		reportContentIssue: reportContentIssue,
		resolveContentIssue: resolveContentIssue,
		searchContentIssues: searchContentIssues,
	};
}

export default createService(client);
